import { FC } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { SelectedCompanyCurrency } from '../../../../components/selected-company-currency/index';
import {
  LiquidityCapTableByStakeholder,
  ScenarioCapTableByStakeholder,
} from '../../../../types/scenario-modeling.types';
import { toRound } from '../../../../utils/get-rounded-number.util';
import { objectTypeCheck } from '../../../../utils/object-type-check.util';
import { toMathRound } from '../../../../utils/to-math-round.util';
import { toNumber } from '../../../../utils/to-number.util';
import { ItemCell } from './item-cell';

type StakeholderItemProps = {
  item: LiquidityCapTableByStakeholder | ScenarioCapTableByStakeholder;
};

export const StakeholderItem: FC<StakeholderItemProps> = ({ item }) => {
  const {
    diluted,
    dilutedPercentage,
    investment,
    issued,
    issuedPercentage,
    shareClasses,
    votingPercentage,
    dilutedChange,
    dilutedPercentageChange,
    investmentChange,
    issuedChange,
    issuedPercentageChange,
    votingPercentageChange,
    stakeholder,
    fakeStakeholderName,
    avgSharePrice,
    avgSharePriceChange,
    voting,
    votingChange,
    convertedBasis,
    convertedBasisChange,
    convertedBasisPercentage,
    convertedBasisPercentageChange,
  } = item;

  const isLiquidity = objectTypeCheck<LiquidityCapTableByStakeholder>(item, 'seniorityColumns');

  let liquidityProceeds = 0;
  let roi = 0;
  if (isLiquidity) {
    liquidityProceeds = item.minLiquidityProceeds;
    roi = item.roi;
  }

  return (
    <>
      <div className="flex w-full flex-col gap-2 rounded-md pt-3 shadow-sm">
        <div className="flex w-full items-center gap-4 border-b-[2px] border-b-gray-100 px-3 pb-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-[450] text-gray-700">
            {fakeStakeholderName ? fakeStakeholderName[0] : stakeholder?.fullName[0]}
          </div>
          <div className="flex flex-col overflow-hidden">
            <span className="max-w-full truncate text-base font-semibold text-gray-700">
              {fakeStakeholderName ? fakeStakeholderName : stakeholder?.fullName}
            </span>
            <span className="text-xs font-[450] text-gray-400">{stakeholder?.email || '-'}</span>
          </div>
        </div>
        <div className="grid grid-flow-row grid-cols-2">
          <ItemCell
            change={issuedPercentageChange}
            title={<AppFormattedMessage id="ISSUED_PERCENTAGE" />}
            value={toRound(toNumber(issuedPercentage) * 100)}
          />
          <ItemCell
            change={dilutedPercentageChange}
            title={<AppFormattedMessage id="DILUTED_PERCENTAGE" />}
            value={toRound(toNumber(dilutedPercentage) * 100)}
          />
          <ItemCell
            change={votingPercentageChange}
            title={<AppFormattedMessage id="VOTING_PERCENTAGE" />}
            value={toRound(toNumber(votingPercentage) * 100)}
          />
          <ItemCell
            change={issuedChange}
            title={<AppFormattedMessage id="NO_ISSUED" />}
            value={issued}
          />
          <ItemCell
            change={dilutedChange}
            title={<AppFormattedMessage id="DILUTED_SHARES" />}
            value={diluted}
          />
          <ItemCell
            change={investmentChange}
            title={
              <>
                <AppFormattedMessage id="INVESTMENT" /> (<SelectedCompanyCurrency />)
              </>
            }
            value={investment}
          />
          <ItemCell
            change={votingChange}
            title={<AppFormattedMessage id="VOTING_RIGHTS" />}
            value={voting}
          />
          <ItemCell
            change={avgSharePriceChange}
            title={<AppFormattedMessage id="AVERAGE_SHARE_PRICE" />}
            value={avgSharePrice}
          />
          <ItemCell
            change={convertedBasisChange}
            title={<AppFormattedMessage id="CONVERTED_BASIS" />}
            value={convertedBasis}
          />
          <ItemCell
            change={convertedBasisPercentageChange}
            title={<AppFormattedMessage id="CONVERTED_BASIS_PERCENT" />}
            value={toRound(toNumber(convertedBasisPercentage) * 100)}
          />
          {isLiquidity && (
            <>
              <ItemCell
                title={<AppFormattedMessage id="LIQUIDITY_PROCEEDS" />}
                value={toNumber(liquidityProceeds)}
              />
              <ItemCell title={<AppFormattedMessage id="ROI" />} value={toMathRound(roi)} />
            </>
          )}
        </div>
        {shareClasses && shareClasses?.length > 0 && (
          <div className="grid grid-flow-row grid-cols-2">
            {shareClasses.map(({ name, issued, issuedChange, id }) => (
              <ItemCell change={issuedChange} key={id} title={name} value={issued} />
            ))}
          </div>
        )}
      </div>
    </>
  );
};
